import React, { useState, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { Button, Card, Spinner, ProgressBar, Modal } from "react-bootstrap";
import Flex from "components/common/Flex";
import cloudUpload from "assets/img/icons/cloud-upload.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf, faEye } from "@fortawesome/free-solid-svg-icons";
import Compressor from "compressorjs";
import { useDispatch, useSelector } from "react-redux";
import IconButton from "components/common/IconButton";
import { Trash } from "react-bootstrap-icons";
import { useLocation } from "react-router-dom";
import {
  handleFillStepData,
  handleResetCreateClaim,
} from "features/cliams/config/claimSlice";
import { CreateSingleClaim, ViewClaim } from "../config/cliamApi.js";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";

const ClaimDocumentRequestModal = ({
  showModal,
  setShowModal,
  row,
  setRow,
}) => {
  const dispatch = useDispatch();
  const { viewClaimInfo } = useSelector((state) => state.claim);
  const { watch, setValue, handleSubmit, setError, clearErrors } = useForm();
  const location = useLocation();
  const isViewRequest = location.pathname.includes("view-claim-request");
  const isDisabled = !isViewRequest;
  const payloadClaim = viewClaimInfo?.payload_json;
  const attachments = Object.values(payloadClaim?.sup_info_attch || {});
  const [files, setFiles] = useState([]);
  const [compressionProgress, setCompressionProgress] = useState(0);

  const EditableStatus =
    row?.clm_status?.toLowerCase() === "invalid" ||
    row?.clm_status?.toLowerCase() === "cancelled" ||
    row?.clm_status?.toLowerCase() === "rejected" ||
    row?.clm_status?.toLowerCase() === "invalid" ||
    row?.clm_status?.toLowerCase() === "ready to submit" ||
    row?.clm_status?.toLowerCase() == "no response" ||
    row?.clm_status?.toLowerCase() == "nores";

  useEffect(() => {
    if (row?.claim_nwid && showModal) {
      dispatch(
        ViewClaim({
          claimid: row?.claim_nwid,
          claim_type: row?.claim_type,
        }),
      );
    }
  }, [dispatch, row, showModal]);

  const attachements = watch("attachments");

  useEffect(() => {
    if (!attachments.length && !viewClaimInfo) return;

    // ✅ Build the normal sup_info_attch files
    const prefilledFiles = attachments
      .filter((entry) => entry?.contentType && entry?.data && entry?.title)
      .map((entry) => {
        const mime = entry.contentType || "application/pdf";
        let blob;
        try {
          blob = new Blob(
            [Uint8Array.from(atob(entry.data), (c) => c.charCodeAt(0))],
            { type: mime },
          );
        } catch (error) {
          console.error(
            `Error decoding base64 for file ${entry.title}:`,
            error,
          );
          return null;
        }

        const file = new File([blob], entry.title, {
          type: mime,
          lastModified: new Date(entry.creation || Date.now()).getTime(),
        });

        return {
          originalFile: file,
          compressedFile: file,
          preview: mime.includes("pdf") ? null : URL.createObjectURL(file),
          type: mime,
          path: entry.title,
          base64: entry.data,
          title: entry.title,
          creation: entry.creation || new Date().toISOString(),
        };
      })
      .filter((file) => file !== null);

    // ✅ NEW CODE for invoice & caf
    const extraFiles = [];
    if (viewClaimInfo?.invoice_attach) {
      extraFiles.push({
        title: "Invoice Attachment",
        type: "application/pdf",
        base64: null,
        externalUrl: viewClaimInfo.invoice_attach,
        preview: null,
        path: "invoice_attachment.pdf",
        creation: new Date().toISOString(),
      });
    }
    if (viewClaimInfo?.caf_attach) {
      extraFiles.push({
        title: "CAF Attachment",
        type: "application/pdf",
        base64: null,
        externalUrl: viewClaimInfo.caf_attach,
        preview: null,
        path: "caf_attachment.pdf",
        creation: new Date().toISOString(),
      });
    }

    const allFiles = [...prefilledFiles, ...extraFiles];
    setFiles(allFiles);

    const attachmentsParsed = prefilledFiles.map((f) => ({
      valueAttachment: {
        contentType: f.type,
        title: f.title,
        creation: f.creation,
        data: f.base64,
      },
    }));

    setValue("attachments", attachmentsParsed);
    dispatch(
      handleFillStepData({
        check: "attachments",
        value: attachmentsParsed,
      }),
    );

    return () =>
      prefilledFiles.forEach(
        (file) => file.preview && URL.revokeObjectURL(file.preview),
      );
  }, [payloadClaim, viewClaimInfo, setValue, dispatch]);

  const compressImage = (file) =>
    new Promise((resolve) => {
      new Compressor(file, {
        quality: 0.6,
        maxWidth: 1920,
        maxHeight: 1920,
        convertSize: 5000000,
        success(compressedFile) {
          resolve({
            file: compressedFile,
            originalSize: file.size,
            compressedSize: compressedFile.size,
          });
        },
        error() {
          resolve({
            file,
            originalSize: file.size,
            compressedSize: file.size,
          });
        },
      });
    });

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64 = reader.result.split(",")[1];
        resolve(base64);
      };
      reader.onerror = reject;
    });

  const handleView = (file) => {
    try {
      if (file.externalUrl) {
        window.open(file.externalUrl, "_blank");
        return;
      }
      const dataUrl = `data:${file.type || "application/pdf"};base64,${
        file.base64
      }`;
      const newWindow = window.open();
      if (newWindow) {
        newWindow.document.write(`
          <html>
            <body style="margin:0">
              ${
                file.type.includes("pdf")
                  ? `<embed src="${dataUrl}" type="${file.type}" width="100%" height="100%" />`
                  : `<img src="${dataUrl}" style="width:100%;height:auto;" />`
              }
            </body>
          </html>
        `);
        newWindow.document.title = file.title || "Document Preview";
      }
    } catch (error) {
      console.error("Error opening file preview:", error);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    accept: {
      "image/*": [".jpeg", ".png", ".jpg"],
      "application/pdf": [".pdf"],
    },
    onDrop: async (acceptedFiles) => {
      setCompressionProgress(0);

      try {
        const processedFiles = await Promise.all(
          acceptedFiles.map(async (originalFile, index) => {
            const sanitizedName = (originalFile.name || "")
              .replace(/\s+/g, "_")
              .replace(/\([^)]*\)/g, "")
              .replace(/[^\w.-]/g, "");

            const compressionResult = await compressImage(originalFile);
            const base64Data = await toBase64(compressionResult.file);
            const ext = sanitizedName
              .substring(sanitizedName.lastIndexOf("."))
              .toLowerCase();
            const creation = new Date().toISOString().split("T")[0];
            const hash = `${Date.now()}-${index}`;
            const title = `${hash}${ext}`;

            return {
              originalFile,
              compressedFile: compressionResult.file,
              preview: compressionResult.file.type.includes("pdf")
                ? null
                : URL.createObjectURL(compressionResult.file),
              type: originalFile.type,
              path: sanitizedName,
              base64: base64Data,
              title,
              creation,
            };
          }),
        );

        const updatedFiles = [...files, ...processedFiles];
        setFiles(updatedFiles);

        const newAttachments = processedFiles.map((f) => ({
          valueAttachment: {
            contentType: f.compressedFile.type,
            title: f.title,
            creation: f.creation,
            data: f.base64,
          },
        }));
        const currentAttachments = watch("attachments") || [];
        const updatedAttachments = [...currentAttachments, ...newAttachments];

        setValue("attachments", updatedAttachments);
        dispatch(
          handleFillStepData({
            check: "attachments",
            value: updatedAttachments,
          }),
        );

        clearErrors("attachments");
      } catch (error) {
        setError("attachments", {
          type: "manual",
          message: "Error processing files",
        });
      }

      setCompressionProgress(100);
    },
    onDropRejected: () => {
      setError("attachments", {
        type: "manual",
        message: "Invalid file type or size (Max 9MB)",
      });
    },
    maxSize: 9 * 1024 * 1024,
  });

  const handleRemove = (path) => {
    const newFiles = files.filter((file) => file.path !== path);
    setFiles(newFiles);

    const updatedAttachments = newFiles
      .filter((f) => !f.externalUrl)
      .map((f) => ({
        valueAttachment: {
          contentType: f.type,
          title: f.title,
          creation: f.creation,
          data: f.base64 || "",
        },
      }));

    setValue("attachments", updatedAttachments);
    dispatch(
      handleFillStepData({
        check: "attachments",
        value: updatedAttachments,
      }),
    );
  };

  const onSubmit = async (data) => {
    const body = {
      ...payloadClaim,
      claim_submit: "",
      claim_id: row?.claim_nwid || "",
      sup_info_attch: {},
    };

    if (Array.isArray(attachements)) {
      attachements.forEach((item, index) => {
        body.sup_info_attch[index + 1] = {
          contentType: item?.valueAttachment?.contentType || "",
          title: item?.valueAttachment?.title || "",
          creation: item?.valueAttachment?.creation || "",
          data: item?.valueAttachment?.data || "",
        };
      });
    }

    const response = await dispatch(CreateSingleClaim(body)).unwrap();
    if (response?.success) {
      toast.success(response?.message_en);
      dispatch(handleResetCreateClaim());
      setShowModal(false);
      setValue("attachments", null);
      setFiles([]);
      setRow(null);
    }
  };

  useEffect(() => {
    return () => {
      files.forEach(
        (file) => file.preview && URL.revokeObjectURL(file.preview),
      );
    };
  }, [files]);

  return (
    <Modal
      show={showModal}
      onHide={() => {
        setShowModal(false);
        setValue("attachments", null);
        setFiles([]);
        dispatch(handleResetCreateClaim());
        setRow(null);
      }}
      size="lg"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title className="fs-10">Upload Documents</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {EditableStatus || files?.length > 0 ? (
          <Card>
            <Card.Body>
              {EditableStatus && (
                <div {...getRootProps({ className: "dropzone-area py-4" })}>
                  <input {...getInputProps({ name: "attachments" })} />
                  <div className={isViewRequest ? "fs-12" : "fs-10"}>
                    {compressionProgress > 0 && compressionProgress < 100 ? (
                      <div className="text-center">
                        <Spinner
                          animation="border"
                          variant="primary"
                          size="sm"
                        />
                        <ProgressBar
                          now={compressionProgress}
                          label={`${compressionProgress}%`}
                          className="mt-2"
                        />
                        <p className="mt-2 mb-0">Compressing files...</p>
                      </div>
                    ) : (
                      <>
                        <img
                          src={cloudUpload}
                          alt=""
                          width={25}
                          className="me-2"
                        />
                        <span className="d-none d-lg-inline">
                          Drag your file here (PDF or Image)
                          <br />
                          or,{" "}
                        </span>
                        <Button
                          variant="link"
                          size="sm"
                          className={isViewRequest ? "p-0 fs-12" : "p-0 fs-10"}
                        >
                          Browse
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              )}

              <div>
                {files.map((file) => (
                  <Flex
                    alignItems="center"
                    className="py-2 border-bottom btn-reveal-trigger"
                    key={file.path}
                  >
                    <div
                      className="rounded d-flex align-items-center justify-content-center"
                      style={{ width: 20, height: 20 }}
                    >
                      <FontAwesomeIcon
                        icon={faFilePdf}
                        className="text-danger"
                      />
                    </div>

                    <Flex
                      justifyContent="between"
                      alignItems="center"
                      className="ms-3 flex-1"
                    >
                      <div>
                        <h6
                          className={isViewRequest ? "mb-0 fs-12" : "mb-0 fs-9"}
                        >
                          {file.title}
                        </h6>
                      </div>
                    </Flex>

                    <IconButton
                      variant="primary"
                      size="sm"
                      onClick={() => handleView(file)}
                      className="ms-2"
                    >
                      <FontAwesomeIcon
                        size={isViewRequest ? 12 : 16}
                        icon={faEye}
                      />
                    </IconButton>

                    {EditableStatus && !file.externalUrl && (
                      <IconButton
                        variant="danger"
                        size="sm"
                        onClick={() => handleRemove(file.path)}
                        className="ms-2"
                      >
                        <Trash size={isViewRequest ? 12 : 16} />
                      </IconButton>
                    )}
                  </Flex>
                ))}
              </div>
            </Card.Body>
          </Card>
        ) : (
          <div className="py-3 border text-center fs-9 ">
            No Documents Found
          </div>
        )}
      </Modal.Body>
      {EditableStatus && (
        <Modal.Footer>
          <Button size="sm" variant="success" onClick={handleSubmit(onSubmit)}>
            Upload Documents
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
};

export default ClaimDocumentRequestModal;
